import { useState, useEffect } from 'react';

interface PlatformInfo {
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isWeb: boolean;
  isCapacitor: boolean;
}

export function usePlatform(): PlatformInfo {
  // Initial check for SSR/hydration safety
  const getInitialPlatform = (): PlatformInfo => {
    try {
      if (typeof window === 'undefined') {
        return {
          isMobile: false,
          isIOS: false,
          isAndroid: false,
          isWeb: true,
          isCapacitor: false,
        };
      }

      const userAgent = navigator.userAgent?.toLowerCase() || '';
      const isIOS = /iphone|ipad|ipod/.test(userAgent);
      const isAndroid = /android/.test(userAgent);
      const isMobile = (window.innerWidth < 1024) || isIOS || isAndroid;
      const isCapacitor = !!(window as unknown as { Capacitor?: unknown }).Capacitor;

      return {
        isMobile,
        isIOS,
        isAndroid,
        isWeb: !isCapacitor,
        isCapacitor,
      };
    } catch (error) {
      console.error('Error in getInitialPlatform:', error);
      // Safe fallback
      return {
        isMobile: false,
        isIOS: false,
        isAndroid: false,
        isWeb: true,
        isCapacitor: false,
      };
    }
  };

  const [platform, setPlatform] = useState<PlatformInfo>(getInitialPlatform);

  useEffect(() => {
    const checkPlatform = () => {
      try {
        const userAgent = navigator.userAgent?.toLowerCase() || '';
        const isIOS = /iphone|ipad|ipod/.test(userAgent);
        const isAndroid = /android/.test(userAgent);
        const isMobile = (window.innerWidth < 1024) || isIOS || isAndroid;
        // Check if running in Capacitor
        const isCapacitor = !!(window as unknown as { Capacitor?: unknown }).Capacitor;

        setPlatform({
          isMobile,
          isIOS,
          isAndroid,
          isWeb: !isCapacitor,
          isCapacitor,
        });
      } catch (error) {
        console.error('Error in checkPlatform:', error);
        // Don't update platform on error, keep current state
      }
    };

    // Run immediately in case initial state was wrong
    checkPlatform();

    // Update on resize
    window.addEventListener('resize', checkPlatform);
    return () => window.removeEventListener('resize', checkPlatform);
  }, []);

  return platform;
}
